import { useState, useEffect } from 'react';

import useGetCombinedSBSChartData from 'hooks/Traps/SRBMTraps/useGetCombinedChartData';

interface AnnotationLabel {
  text: string;
  borderColor?: string;
  style?: {
    color?: string;
    background?: string;
  };
}

interface ChartData {
  date_time: string[];
  bait_filling: number[];
  eaten_bait: number[];
  is_added: boolean[];
  added_bait: number[];
  bait_replacement: boolean[];
  bait_add_btn: boolean[];
  annotations?: {
    x: number;
    label: AnnotationLabel;
    strokeDashArray: number;
    borderColor: string;
  }[];
  minMaxSelection: number[];
}

interface UseChartDataProps {
  slug: string;
}

export const useCombinedChartState = ({ slug }: UseChartDataProps) => {
  const [isPercents, setIsPercents] = useState<boolean>(false);
  const [showLabels, setShowLabels] = useState<boolean>(false);
  const { data, refetch, isLoading, isFetching } = useGetCombinedSBSChartData(slug, isPercents);

  const [chartData, setChartData] = useState<ChartData>({
    date_time: [],
    bait_filling: [],
    eaten_bait: [],
    is_added: [],
    added_bait: [],
    bait_replacement: [],
    bait_add_btn: [],
    annotations: [],
    minMaxSelection: [],
  });

  useEffect(() => {
    if (data) {
      const date_time = data.map((item: any) => item.date_time); // Keep raw datetime
      const bait_filling = data.map((item: any) => item.bait_filling);
      const eaten_bait = data.map((item: any) => item.eaten_bait);
      const is_added = data.map((item: any) => item.is_added);
      const added_bait = data.map((item: any) => item.added_bait);
      const bait_replacement = data.map((item: any) => item.bait_replacement);
      const bait_add_btn = data.map((item: any) => item.bait_add_btn);

      const annotations = data.reduce((result: any[], item: any) => {
        if (item.bait_replacement) {
          result.push({
            x: Date.parse(item.date_time),
            strokeDashArray: 2,
            borderColor: '#6A37D4',
            label: {
              text: 'Bait replacement',
              // orientation: 'horizontal',
              position: 'center',
              borderColor: '#6A37D4',
              style: {
                color: '#fae600',
                background: '#6A37D4',
              },
            },
          });
        }

        if (item.bait_add_btn && !item.bait_replacement) {
          result.push({
            x: Date.parse(item.date_time),
            strokeDashArray: 0,
            borderColor: '#fae600',
            opacity: 0.3,
            label: {
              offsetX: 0,
              borderColor: '#fae600',
              style: {
                color: '#6A37D4',
                background: '#fae600',
              },
              position: 'center',
              text: `+ ${item.added_bait} ${isPercents ? '%' : 'g.'}`,
            },
          });
        }
        return result;
      }, []);

      const minMaxSelection = [];
      if (data.length >= 10) {
        const lastTenSignals = data.slice(data.length - 10);

        minMaxSelection.push(
          Date.parse(lastTenSignals[0].date_time),
          Date.parse(lastTenSignals[lastTenSignals.length - 1].date_time),
        );
      } else if (data.length > 0) {
        minMaxSelection.push(Date.parse(data[0].date_time), Date.parse(data[data.length - 1].date_time));
      }

      setChartData({
        date_time,
        bait_filling,
        eaten_bait,
        is_added,
        added_bait,
        bait_replacement,
        bait_add_btn,
        annotations,
        minMaxSelection,
      });
    }
  }, [data, isPercents, showLabels]);

  useEffect(() => {
    refetch();
  }, [isPercents, refetch]);

  return {
    chartData,
    isFetching,
    isLoading,
    refetch,
    setIsPercents,
    isPercents,
    showLabels,
    setShowLabels,
  };
};
